import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ShieldCheck, FlaskConical } from "lucide-react";
import { useAuth, formatAuthError } from "../context/AuthContext";

export default function Login() {
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from || "/products";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Already-logged-in visitor → bounce to their original destination.
  useEffect(() => {
    if (isAuthenticated) navigate(redirectTo, { replace: true });
  }, [isAuthenticated, redirectTo, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email, password);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(formatAuthError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main
      data-testid="login-page"
      className="min-h-[calc(100vh-7rem)] bg-cream-200/40 py-14 md:py-20"
    >
      <div className="container-x max-w-md">
        <div className="mb-8 text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-sage-100 flex items-center justify-center mb-5">
            <FlaskConical className="w-5 h-5 text-sage-600" strokeWidth={1.8} />
          </div>
          <p className="overline mb-2">Researcher Account</p>
          <h1 className="font-display text-4xl md:text-5xl font-bold text-charcoal-600 leading-tight">
            Welcome back
          </h1>
          <p className="mt-3 text-sm text-charcoal-400">
            Sign in to continue ordering research compounds.
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="surface-card p-8 md:p-10 space-y-5"
          data-testid="login-form"
        >
          <label htmlFor="email" className="block">
            <span className="block text-xs font-medium uppercase tracking-[0.18em] text-charcoal-400 mb-2">
              Email
            </span>
            <input
              id="email"
              data-testid="login-email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input"
            />
          </label>

          <label htmlFor="password" className="block">
            <span className="block text-xs font-medium uppercase tracking-[0.18em] text-charcoal-400 mb-2">
              Password
            </span>
            <input
              id="password"
              data-testid="login-password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input"
            />
          </label>

          {error && (
            <p data-testid="login-error" className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
              {error}
            </p>
          )}

          <button
            type="submit"
            data-testid="login-submit"
            disabled={submitting}
            className="btn-primary w-full disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <ShieldCheck className="w-4 h-4 mr-2" strokeWidth={2} />
            {submitting ? "Signing in…" : "Sign in"}
          </button>

          <div className="text-center">
            <Link
              to="/forgot-password"
              data-testid="login-forgot-password"
              className="text-sm font-medium text-[#8B2252] hover:underline underline-offset-4"
            >
              Forgot your password? Reset it here
            </Link>
          </div>

          <p className="text-center text-sm text-charcoal-400">
            Don't have an account?{" "}
            <Link
              to="/register"
              data-testid="login-go-to-register"
              className="text-sage-700 hover:text-sage-800 font-medium"
            >
              Create one
            </Link>
          </p>
        </form>
      </div>
    </main>
  );
}
